import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { safeParse, safeStringify, sentryError } from 'utils';
import { getIsEditorValueEmpty } from 'components/RichTextEditorField/utils';
import RichTextEditorField from 'components/RichTextEditorField';
import { RICH_TEXT_EDITOR_INITIAL_VALUE } from 'components/RichTextEditorField/RichTextEditorField.component';
import { TagOption } from 'components/RichTextEditorField/components/TagOptions/TagOptions.component';
import ClipIcon from 'icons/Clip.icon';
import ResolvePopup from 'components/ResolvePopup';
import UploadingFiles from './components/UploadingFiles';
import { UploaderMessageFile } from 'hooks/useUploadMessageFile';
import useRichTextEditorStyles from './hooks/useRichTextEditorStyles';

import './MessageForm.styles.scss';

const MAX_NUM_OF_CHAR = 100000;

type MessageFormProps = PropsWithChildren<{
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  buttonText?: string;
  canTagPeople?: boolean;
  resetMessage?: string | number | boolean;
  uploadFileHintMessage?: string;
  tagOptions?: TagOption[];
  files?: UploaderMessageFile[];
  areAllFilesProcessed?: boolean;
  onFileRemove?: (id: string) => void;
  onFileUpload?: React.MouseEventHandler<HTMLElement>;
  handleOnError?: () => void;
  handleOnSubmit: (value: string) => Promise<void>;
}>;

const MessageForm: FC<MessageFormProps> = ({
  className,
  children,
  disabled,
  placeholder,
  buttonText,
  resetMessage,
  canTagPeople,
  tagOptions,
  uploadFileHintMessage,
  files = [],
  areAllFilesProcessed = false,
  onFileRemove,
  onFileUpload,
  handleOnError,
  handleOnSubmit,
}) => {
  const { t } = useTranslation();

  const classes = classNames('message-form', className);

  const ref = useRef(null);

  const hasFiles = !!files?.length;

  const { formRef, richTextRef } = useRichTextEditorStyles(disabled, hasFiles);

  const { popServerError } = useContext(PopNotificationsContext);

  const resetForm = useCallback(() => {
    if (ref?.current) {
      ref.current.reset();
      ref.current.restart();
    }
  }, []);

  const onSubmit = useCallback(
    async ({ message }) => {
      try {
        if (disabled || !message) return;

        await handleOnSubmit(message.trim());

        resetForm();
      } catch (e) {
        sentryError(e);
        popServerError(e);
        handleOnError?.();
      }
    },
    [disabled, resetForm, handleOnError, handleOnSubmit, popServerError],
  );

  useEffect(() => {
    resetForm();
  }, [resetMessage, resetForm]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        message: safeStringify(RICH_TEXT_EDITOR_INITIAL_VALUE),
      }}
      render={({
        pristine,
        form,
        hasValidationErrors,
        submitting,
        values: { message },
        handleSubmit,
      }) => {
        ref.current = form;

        return (
          <form className={classes} ref={formRef} onSubmit={handleSubmit}>
            <div className="message-form__wrapper" ref={richTextRef}>
              <RichTextEditorField
                id="message-form-input"
                name="message"
                required
                showError={false}
                showMaxLength={false}
                readOnly={disabled}
                maxLength={MAX_NUM_OF_CHAR}
                {...(pristine && { placeholder })}
                {...(canTagPeople && { tagOptions })}
              />
              {hasFiles && (
                <UploadingFiles files={files} onRemove={onFileRemove} />
              )}
            </div>

            <div className="message-form__actions">
              {children}

              {!disabled &&
                onFileUpload &&
                (uploadFileHintMessage ? (
                  <ResolvePopup onHover popupContent={uploadFileHintMessage}>
                    <Button
                      variant="icon"
                      disabled={disabled}
                      icon={ClipIcon}
                      onClick={onFileUpload}
                    />
                  </ResolvePopup>
                ) : (
                  <Button
                    variant="icon"
                    disabled={disabled}
                    icon={ClipIcon}
                    onClick={onFileUpload}
                  />
                ))}

              <Button
                type="submit"
                className="send-button"
                disabled={
                  disabled ||
                  hasValidationErrors ||
                  (getIsEditorValueEmpty(safeParse(message)) &&
                    !files.length) ||
                  (!!files.length && !areAllFilesProcessed)
                }
              >
                {submitting ? <Loader size="small" /> : buttonText || t('save')}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default MessageForm;
